import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

interface SlideCardProps {
  quote: string;
  profilePicture: string;
  name: string;
  title: string;
  companyName: string;
  companyLogo?: string;
  link?: string;
}

const SlideCard: React.FC<SlideCardProps> = ({
  quote,
  profilePicture,
  name,
  title,
  companyName,
  companyLogo,
  link,
}) => {
  const handleClick = () => {
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Styled.Container onClick={handleClick} clickable={!!link}>
      <Styled.QuoteContainer>
        <blockquote>"{quote}"</blockquote>
      </Styled.QuoteContainer>

      <Styled.ProfileContainer>
        <Styled.ProfilePicture>
          <img src={profilePicture} alt={`${name} profile picture`} />
        </Styled.ProfilePicture>

        <Styled.InfoContainer>
          <Styled.Name>{name}</Styled.Name>
          <Styled.Title>{title}</Styled.Title>
          <Styled.CompanyContainer>
            {companyLogo && (
              <Styled.CompanyLogo>
                <img src={companyLogo} alt={`${companyName} logo`} />
              </Styled.CompanyLogo>
            )}
            <Styled.CompanyName>{companyName}</Styled.CompanyName>
          </Styled.CompanyContainer>
        </Styled.InfoContainer>
      </Styled.ProfileContainer>
    </Styled.Container>
  );
};

export default SlideCard;

/* Styled Components
======================================================= */
let Styled: any = {};

Styled.Container = styled.div<{ clickable: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      padding: 40px;
      background-color: ${t.color.white};
      border-radius: 7px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      cursor: ${props.clickable ? "pointer" : "default"};
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      max-width: 490px;
      min-width: 400px;
      margin: 0 auto;
      height: 300px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      &:hover {
        transform: ${props.clickable ? "translateY(-2px)" : "none"};
        box-shadow: ${props.clickable
          ? "0 8px 32px rgba(0, 0, 0, 0.12)"
          : "0 4px 20px rgba(0, 0, 0, 0.08)"};
      }
    `;
  }}
`;

Styled.QuoteContainer = styled.div`
  ${(props) => {
    const t: any = props.theme;
    return css`
      width: 100%;
      margin-bottom: 32px;

      blockquote {
        margin: 0;
        font-size: 20px;
        line-height: 1.5;
        color: ${t.color.gray[800]};
        font-weight: 400;
        font-style: normal;

        p {
          margin: 0;
        }
      }
    `;
  }}
`;

Styled.ProfileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

Styled.ProfilePicture = styled.div`
  ${(props) => {
    const t: any = props.theme;
    return css`
      width: 64px;
      height: 64px;
      border-radius: 8px;
      overflow: hidden;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    `;
  }}
`;

Styled.InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

Styled.Name = styled.h3`
  ${(props) => {
    const t: any = props.theme;
    return css`
      font-weight: 500;
      font-size: 18px;
      color: ${t.color.gray[900]};
      margin: 0;
    `;
  }}
`;

Styled.Title = styled.p`
  ${(props) => {
    const t: any = props.theme;
    return css`
      font-size: 16px;
      color: ${t.color.gray[600]};
      margin: 0;
      font-weight: 400;
    `;
  }}
`;

Styled.CompanyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`;

Styled.CompanyLogo = styled.div`
  width: 24px;
  height: 24px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

Styled.CompanyName = styled.div`
  ${(props) => {
    const t: any = props.theme;
    return css`
      font-size: 15px;
      color: ${t.color.gray[600]};
      font-weight: 500;
    `;
  }}
`;
